import type { FloorplanMarker } from "@admin/workspaces/types";

const SVG_NS = "http://www.w3.org/2000/svg";

export function isExportNoiseGroupId(id: string): boolean {
    if (!id || id === "Layer_1" || id === "fp-overlays" || id === "fp-labels") return true;
    if (/^defs\d*$/i.test(id) || /^namedview\d*$/i.test(id)) return true;
    if (/^g\d+$/i.test(id)) return true;
    return false;
}

/** Lahore desks use bare numeric ids (`114`); Franklin uses `Desk001`. */
export function isDeskGroupId(id: string): boolean {
    return /^(?:Desk)?\d+$/i.test(id);
}

/**
 * Resolve an SVG node by id. Prefer getElementById, then an attribute
 * selector — numeric-only ids (e.g. `114`) are valid in SVG but can be
 * missed by CSS `#id` selectors when the markup is inlined into HTML.
 */
export function findSvgElementById(
    svg: SVGSVGElement,
    id: string,
): Element | null {
    if (!id) return null;
    const direct = svg.getElementById(id);
    if (direct) return direct;
    try {
        return svg.querySelector(`[id="${CSS.escape(id)}"]`);
    } catch {
        return null;
    }
}

export function isHoverableSpaceId(id: string): boolean {
    if (!id) return false;
    if (id === "Layer_1" || id === "fp-overlays") return false;
    if (/^defs\d*$/i.test(id) || /^namedview\d*$/i.test(id)) return false;
    if (/^svg\d*$/i.test(id) || /^metadata\d*$/i.test(id)) return false;
    if (
        /^(clipPath|mask|linearGradient|radialGradient|filter|pattern|marker|symbol)/i.test(
            id,
        )
    ) {
        return false;
    }
    if (id.startsWith("fp-")) return false;
    return true;
}

export function hoverOutlineRootEl(
    interactiveEl: Element,
    svgRoot: SVGSVGElement | null,
): Element | null {
    if (!interactiveEl) return null;
    if (interactiveEl.tagName.toLowerCase() === "g") return interactiveEl;
    let n: Element | null = interactiveEl.parentElement;
    while (n && n !== svgRoot) {
        if (n.tagName.toLowerCase() === "g") return n;
        n = n.parentElement;
    }
    return interactiveEl;
}

function groupAlreadyHasHitSurface(g: Element): boolean {
    for (let c = g.firstElementChild; c; c = c.nextElementSibling) {
        if (c.getAttribute("data-fp-hit") === "1") return true;
    }
    return false;
}

export function installDeskGroupHitSurfaces(svg: SVGSVGElement): void {
    const seen = new WeakSet<Element>();
    for (const el of svg.querySelectorAll(".fp-assigned, .fp-zone")) {
        const gRoot = hoverOutlineRootEl(el, svg);
        if (!gRoot || gRoot.tagName.toLowerCase() !== "g" || seen.has(gRoot)) continue;
        seen.add(gRoot);
        if (groupAlreadyHasHitSurface(gRoot)) continue;

        // Prefer the real shape bbox (ignore prior chrome).
        const shape = groupShapeEl(gRoot);
        if (shape) shape.classList.add("fp-shape");

        let bb: DOMRect;
        try {
            bb = ((shape ?? gRoot) as SVGGraphicsElement).getBBox();
        } catch {
            continue;
        }
        if (!bb.width || !bb.height) continue;

        const pad = 1;
        const rect = document.createElementNS(SVG_NS, "rect");
        rect.setAttribute("data-fp-hit", "1");
        rect.setAttribute("data-fp-desk-id", gRoot.id);
        rect.setAttribute("class", "fp-hit-surface");
        rect.setAttribute("x", String(bb.x - pad));
        rect.setAttribute("y", String(bb.y - pad));
        rect.setAttribute("width", String(bb.width + pad * 2));
        rect.setAttribute("height", String(bb.height + pad * 2));
        rect.setAttribute("fill", "#ffffff");
        rect.setAttribute("fill-opacity", "0");
        rect.setAttribute("stroke", "none");
        rect.setAttribute("pointer-events", "none");
        // Must stay in layout for bbox hit-testing; CSS keeps it unpainted.
        rect.setAttribute("visibility", "visible");
        // Keep behind the real shape so highlights follow path geometry.
        const front = gRoot.firstElementChild;
        if (front) gRoot.insertBefore(rect, front);
        else gRoot.appendChild(rect);
    }
}

function groupShapeEl(g: Element): Element | null {
    for (let c = g.firstElementChild; c; c = c.nextElementSibling) {
        if (c.getAttribute("data-fp-hit") === "1") continue;
        if (c.classList.contains("fp-group-label")) continue;
        const tag = c.tagName.toLowerCase();
        if (
            tag === "path" ||
            tag === "rect" ||
            tag === "polygon" ||
            tag === "circle" ||
            tag === "ellipse" ||
            tag === "polyline"
        ) {
            return c;
        }
    }
    return null;
}

export function markInteractiveElements(
    svg: SVGSVGElement,
    markers: readonly FloorplanMarker[],
): void {
    const rosterIds = new Set(markers.map((m) => m.elementId));
    const byId = new Map(markers.map((m) => [m.elementId, m]));

    for (const id of rosterIds) {
        const el = findSvgElementById(svg, id);
        if (!el) continue;
        el.classList.add("fp-assigned");
        const m = byId.get(id);
        if (!m || m.kind !== "desk") continue;
        el.classList.toggle("fp-occupied", m.occupied);
        el.classList.toggle("fp-vacant", !m.occupied);
    }

    for (const g of svg.querySelectorAll("g[id]")) {
        if (rosterIds.has(g.id)) continue;
        if (isExportNoiseGroupId(g.id)) continue;
        g.classList.add("fp-zone");
    }

    installDeskGroupHitSurfaces(svg);
    installGroupNameLabels(svg, markers);
}

/** Human-readable group title: `Board_Room` → `Board Room`, `Desk001` / `114` → `Desk 001` / `114`. */
export function formatFloorplanGroupLabel(id: string): string {
    const desk = /^(?:Desk)?(\d+)$/i.exec(id);
    if (desk) {
        // Bare numeric Lahore ids stay as-is; Desk001 → Desk 001.
        if (/^\d+$/.test(id)) return id;
        return `Desk ${desk[1]}`;
    }
    // Calling_POD01 → Calling POD 01; Interview_Room1 → Interview Room 1
    return id
        .replaceAll("_", " ")
        .replace(/([A-Za-z])(\d+)/g, "$1 $2")
        .replace(/\s+/g, " ")
        .trim();
}

function shouldLabelGroup(id: string): boolean {
    if (isExportNoiseGroupId(id)) return false;
    if (id.startsWith("fp-")) return false;
    // Training rooms stay unlabeled per product request.
    if (/^Training_Room/i.test(id)) return false;
    // Label every meaningful named group: desks, rooms with/without underscores
    // (Kitchen, Gaming, Store, Terrace03, Board_Room, …).
    return true;
}

function resolveGroupLabel(
    id: string,
    markersById: ReadonlyMap<string, FloorplanMarker>,
): string {
    const marker = markersById.get(id);
    const custom = marker?.label?.trim();
    if (custom) return custom;
    return formatFloorplanGroupLabel(id);
}

function wrapLabelWords(
    words: readonly string[],
    fontSize: number,
    maxWidth: number,
    measure: (line: string, fontSize: number) => number,
): string[] | null {
    if (words.length === 0) return [];
    const lines: string[] = [];
    let current = "";
    for (const word of words) {
        const candidate = current === "" ? word : `${current} ${word}`;
        if (measure(candidate, fontSize) <= maxWidth) {
            current = candidate;
            continue;
        }
        if (current === "") {
            // Single word wider than the box — cannot fit at this size.
            return null;
        }
        lines.push(current);
        if (measure(word, fontSize) > maxWidth) return null;
        current = word;
    }
    if (current !== "") lines.push(current);
    return lines;
}

/**
 * Font caps were tuned on Lahore (`viewBox` ~1588 wide). Franklin is
 * ~5100 wide, so the same 6-unit desk cap reads ~3× smaller on screen.
 * Scale min/max font by SVG width so both maps stay readable.
 */
const LABEL_FONT_REF_WIDTH = 1588;

function labelFontScale(svg: SVGSVGElement): number {
    const vb = svg.viewBox.baseVal;
    const width =
        vb && vb.width > 0
            ? vb.width
            : parseFloat(svg.getAttribute("width") ?? "") || LABEL_FONT_REF_WIDTH;
    return Math.min(8, Math.max(0.5, width / LABEL_FONT_REF_WIDTH));
}

/**
 * Draw each named group's label centered inside that group's bbox.
 * Labels live in a top overlay so they stay visible above floor art.
 * Labels prefer assigned user / friendly name from {@link FloorplanMarker.label}.
 */
export function installGroupNameLabels(
    svg: SVGSVGElement,
    markers: readonly FloorplanMarker[] = [],
): void {
    for (const old of svg.querySelectorAll(".fp-group-label")) {
        old.remove();
    }
    svg.getElementById("fp-labels")?.remove();

    const markersById = new Map(markers.map((m) => [m.elementId, m]));
    const fontScale = labelFontScale(svg);

    const layer = document.createElementNS(SVG_NS, "g");
    layer.setAttribute("id", "fp-labels");
    layer.setAttribute("class", "fp-labels");
    layer.setAttribute("pointer-events", "none");

    const measureEl = document.createElementNS(SVG_NS, "text");
    measureEl.setAttribute("class", "fp-group-label");
    measureEl.setAttribute("visibility", "hidden");
    measureEl.setAttribute("aria-hidden", "true");
    // Keep measure node in-tree for accurate text metrics.
    layer.appendChild(measureEl);
    svg.appendChild(layer);

    const measure = (line: string, fontSize: number): number => {
        measureEl.setAttribute("font-size", String(fontSize));
        measureEl.textContent = line;
        try {
            const w = measureEl.getComputedTextLength();
            return w > 0 ? w : line.length * fontSize * 0.56;
        } catch {
            return line.length * fontSize * 0.56;
        }
    };

    for (const g of svg.querySelectorAll("g[id]")) {
        if (!shouldLabelGroup(g.id)) continue;

        const shape = groupShapeEl(g);
        let bb: DOMRect;
        try {
            bb = ((shape ?? g) as SVGGraphicsElement).getBBox();
        } catch {
            continue;
        }
        if (!(bb.width > 2) || !(bb.height > 2)) continue;

        const label = resolveGroupLabel(g.id, markersById);
        if (label === "") continue;

        const isDesk = isDeskGroupId(g.id);
        const hasOccupant = Boolean(markersById.get(g.id)?.occupied);
        const padX = Math.max(1, bb.width * (isDesk ? 0.05 : 0.1));
        const padY = Math.max(1, bb.height * (isDesk ? 0.05 : 0.1));
        const maxW = Math.max(3, bb.width - padX * 2);
        const maxH = Math.max(3, bb.height - padY * 2);
        const words = label.split(/\s+/).filter((w) => w.length > 0);

        const maxFont = Math.min(
            (isDesk ? (hasOccupant ? 5.5 : 6) : 10) * fontScale,
            maxH * (isDesk ? 0.36 : 0.32),
            maxW * (isDesk ? 0.3 : 0.24),
        );
        const minFont = Math.min((isDesk ? 2.25 : 2.75) * fontScale, maxFont);
        let bestSize = minFont;
        let bestLines: string[] = words.length <= 2 ? [...words] : [label];

        if (maxFont >= minFont) {
            let lo = minFont;
            let hi = maxFont;
            for (let i = 0; i < 12; i++) {
                const mid = (lo + hi) / 2;
                const lines = wrapLabelWords(words, mid, maxW, measure);
                if (lines === null) {
                    hi = mid;
                    continue;
                }
                const lineHeight = mid * 1.15;
                const blockH = lines.length * lineHeight;
                if (blockH <= maxH) {
                    bestSize = mid;
                    bestLines = lines;
                    lo = mid;
                } else {
                    hi = mid;
                }
            }
        }

        // Always show a name — fall back to a single compressed line if needed.
        if (bestLines.length === 0) bestLines = [label];
        const fitted = wrapLabelWords(words, bestSize, maxW, measure);
        if (fitted !== null) bestLines = fitted;

        const lineHeight = bestSize * 1.15;
        const blockH = bestLines.length * lineHeight;
        const cx = bb.x + bb.width / 2;
        const cy = bb.y + bb.height / 2;
        const firstBaseline = cy - blockH / 2 + lineHeight * 0.8;

        const text = document.createElementNS(SVG_NS, "text");
        text.setAttribute(
            "class",
            isDesk
                ? hasOccupant
                    ? "fp-group-label fp-desk-label fp-occupant-label"
                    : "fp-group-label fp-desk-label"
                : "fp-group-label",
        );
        text.setAttribute("text-anchor", "middle");
        text.setAttribute("x", String(cx));
        text.setAttribute("y", String(firstBaseline));
        text.setAttribute("font-size", String(bestSize));
        text.setAttribute("stroke-width", String(Math.max(0.9, bestSize * 0.22)));
        text.setAttribute("pointer-events", "none");
        text.setAttribute("data-fp-label-for", g.id);

        bestLines.forEach((line, index) => {
            const tspan = document.createElementNS(SVG_NS, "tspan");
            tspan.setAttribute("x", String(cx));
            if (index === 0) {
                tspan.setAttribute("y", String(firstBaseline));
            } else {
                tspan.setAttribute("dy", String(lineHeight));
            }
            tspan.textContent = line;
            text.appendChild(tspan);
        });

        layer.appendChild(text);
    }

    measureEl.remove();
}

export function refreshOccupancyClasses(
    svg: SVGSVGElement,
    markers: readonly FloorplanMarker[],
): void {
    for (const m of markers) {
        const el = findSvgElementById(svg, m.elementId);
        if (!el || m.kind !== "desk") continue;
        el.classList.add("fp-assigned");
        el.classList.toggle("fp-occupied", m.occupied);
        el.classList.toggle("fp-vacant", !m.occupied);
    }
}

export interface DeskHitZone {
    readonly logicalId: string;
    readonly boundsEl: Element;
    readonly targetEl: Element;
}

export function buildDeskHitZones(
    svg: SVGSVGElement,
    matches: (el: Element | null) => boolean,
): DeskHitZone[] {
    const byId = new Map<string, DeskHitZone>();

    for (const slab of svg.querySelectorAll('rect.fp-hit-surface[data-fp-hit="1"]')) {
        const lid = slab.getAttribute("data-fp-desk-id")?.trim();
        if (!lid) continue;
        const tgt = findSvgElementById(svg, lid);
        if (tgt === null || !matches(tgt)) continue;
        // Prefer the real path/rect for bounds when present (still an AABB in
        // screen space, but matches the shape extent more closely).
        const shape = groupShapeEl(tgt) ?? slab;
        byId.set(lid, { logicalId: lid, boundsEl: shape, targetEl: tgt });
    }

    for (const el of svg.querySelectorAll(".fp-assigned, .fp-zone")) {
        const id = el.id;
        if (!id || byId.has(id)) continue;

        const tag = el.tagName.toLowerCase();
        let boundsEl: Element = el;
        if (tag !== "g") {
            const pg = hoverOutlineRootEl(el, svg);
            if (
                pg &&
                pg.tagName.toLowerCase() === "g" &&
                pg.id &&
                matches(pg)
            ) {
                boundsEl = pg;
            }
        }
        if (!matches(el)) continue;
        byId.set(id, { logicalId: id, boundsEl, targetEl: el });
    }

    return [...byId.values()];
}

export function pickDeskUnderClientPoint(
    zones: readonly DeskHitZone[],
    clientX: number,
    clientY: number,
    matches: (el: Element | null) => boolean,
): Element | null {
    if (!zones.length) return null;
    const ε = 0.25;
    const hits: { zone: DeskHitZone; area: number; precise: boolean }[] = [];
    for (const zone of zones) {
        let br: DOMRect;
        try {
            br = zone.boundsEl.getBoundingClientRect();
        } catch {
            continue;
        }
        const w = Math.abs(br.width || br.right - br.left);
        const h = Math.abs(br.height || br.bottom - br.top);
        if (w < ε || h < ε) continue;
        if (
            clientX < br.left - ε ||
            clientX > br.right + ε ||
            clientY < br.top - ε ||
            clientY > br.bottom + ε
        ) {
            continue;
        }

        // Prefer real path/rect geometry when available (baths, pods, rooms).
        // Never exclude on a failed fill test — transparent SVG fills are flaky
        // across engines; use precision only as a ranking signal.
        const shape = groupShapeEl(zone.targetEl) ?? zone.targetEl;
        const precise = clientPointInShape(shape, clientX, clientY) === true;

        hits.push({
            zone,
            area: Math.max(1e-9, w * h),
            precise,
        });
    }
    if (!hits.length) return null;
    // Precise shape hits first, then smallest bbox (desks beat large rooms).
    hits.sort((a, b) => {
        if (a.precise !== b.precise) return a.precise ? -1 : 1;
        return a.area - b.area;
    });
    const first = hits[0];
    if (!first) return null;
    const t = first.zone.targetEl;
    return matches(t) ? t : null;
}

/** true / false when geometry can decide; null when unknown (use bbox only). */
function clientPointInShape(
    el: Element,
    clientX: number,
    clientY: number,
): boolean | null {
    if (!(el instanceof SVGElement)) return null;
    const svg = el.ownerSVGElement;
    if (!svg?.createSVGPoint) return null;
    const tag = el.tagName.toLowerCase();
    if (
        tag !== "path" &&
        tag !== "rect" &&
        tag !== "polygon" &&
        tag !== "circle" &&
        tag !== "ellipse" &&
        tag !== "polyline"
    ) {
        return null;
    }
    const pt = svg.createSVGPoint();
    pt.x = clientX;
    pt.y = clientY;
    const ctm = (el as SVGGraphicsElement).getScreenCTM();
    if (!ctm) return null;
    let local: DOMPoint;
    try {
        local = pt.matrixTransform(ctm.inverse());
    } catch {
        return null;
    }
    const geo = el as SVGGeometryElement;
    try {
        if (typeof geo.isPointInFill === "function" && geo.isPointInFill(local)) {
            return true;
        }
        if (typeof geo.isPointInStroke === "function" && geo.isPointInStroke(local)) {
            return true;
        }
        // Transparent fills still have geometry; some engines need fill for hit.
        // Fall back to bbox already applied by caller when both return false.
        return false;
    } catch {
        return null;
    }
}
